import { Search, X } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GAP = 4;
const WIDTH = 300;

/**
 * A small search button beside a column heading: it opens a search box, and the results update
 * as you type. Highlighted while there's a search. Escape or Enter closes the box.
 */
export function ColumnSearch({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const searching = value.trim() !== "";

  const place = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ top: rect.bottom + GAP, left: Math.max(8, Math.min(rect.left, window.innerWidth - WIDTH - 8)) });
  }, []);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    input.current?.focus();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!box.current?.contains(target) && !trigger.current?.contains(target)) close(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, place, close]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={searching ? "col-filter col-filter-active" : "col-filter"}
        aria-expanded={open}
        aria-controls={open ? `${id}-box` : undefined}
        aria-label={searching ? `${label}: "${value.trim()}"` : label}
        title={searching ? `${label}: "${value.trim()}"` : label}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Search size={14} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={box}
              id={`${id}-box`}
              role="search"
              className="col-search"
              style={position ? { top: position.top, left: position.left, width: WIDTH } : { visibility: "hidden" }}
            >
              <Search className="col-search-icon" size={16} aria-hidden="true" />
              <input
                ref={input}
                className="input"
                type="text"
                aria-label={label}
                placeholder={placeholder}
                value={value}
                maxLength={200}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape" || event.key === "Enter") {
                    event.preventDefault();
                    close();
                  }
                }}
              />
              {searching ? (
                <button type="button" className="icon-btn icon-btn-sm" aria-label="Clear search" onClick={() => onChange("")}>
                  <X size={14} aria-hidden="true" />
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
